// src/components/BackToLoyalShiftToast.js
// Floating element in bottom-left asking user if they want to return to main LoyalShift site.

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FiX, FiCheck, FiLogOut, FiAlertTriangle } from 'react-icons/fi';

// --- SVG IMPORT (CRA v4 / SVGR Method) ---
// WARNING: This import method only works with Create React App v4
// or projects explicitly configured to support it (e.g., with SVGR/Webpack).
// If using standard CRA v5+, use: import logoUrl from '../logo.svg';
// and render as <img src={logoUrl} ... /> inside the component.
import { ReactComponent as LoyalShiftLogo } from "../logo.svg"; // Adjust path as needed!

// Reusable Button (ensure path is correct)
import Button from './Button'; // Assuming Button is in the same components folder

// --- Theme Colors (Using a dark theme for contrast, similar to main header) ---
const colors = {
  background: "bg-slate-800", // Dark background for toaster/modal
  textPrimary: "text-slate-100",
  textSecondary: "text-slate-400",
  border: "border-slate-700",
  overlayBg: "bg-black/60", // Modal overlay
  confirmButtonBg: "bg-blue-600", // Main LoyalShift blue
  confirmButtonHoverBg: "hover:bg-blue-700",
  cancelButtonBg: "bg-slate-600",
  cancelButtonHoverBg: "hover:bg-slate-700",
  textOnDarkButton: "text-white",
};

// --- Animation Variants ---
const toastVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 100, damping: 15, delay: 0.5 } },
    exit: { opacity: 0, x: -50, transition: { duration: 0.2 } }
};

const modalOverlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
};

const modalContentVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 15, stiffness: 120 } },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } }
};

// --- Main Component ---
const BackToLoyalShiftToast = ({ targetUrl = "/" }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const navigate = useNavigate();

    const openDialog = () => setIsDialogOpen(true);
    const closeDialog = () => setIsDialogOpen(false);

    const handleConfirmNavigation = () => {
        console.log(`Navigating back to LoyalShift main site: ${targetUrl}`);
        closeDialog();
        // Perform navigation after modal closes (optional slight delay)
        setTimeout(() => navigate(targetUrl), 50);
    };

    return (
        <>
            {/* Floating Toaster Element */}
            <motion.div
                className={`fixed bottom-5 left-5 z-40 flex items-center gap-3 p-3 rounded-lg shadow-lg ${colors.background} border ${colors.border} cursor-pointer`}
                variants={toastVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={openDialog} // Open dialog on click
                whileHover={{ scale: 1.03 }}
                transition={{ type: 'spring', stiffness: 300 }}
                role="button" // Indicate it's clickable
                tabIndex={0} // Make it focusable
                onKeyPress={(e) => e.key === 'Enter' && openDialog()} // Allow activation with Enter key
                aria-haspopup="dialog"
                aria-expanded={isDialogOpen}
                aria-label="Volver a LoyalShift"
            >
                {/* Use imported ReactComponent Logo */}
                <LoyalShiftLogo className="w-6 h-6 flex-shrink-0" />
                <span className={`hidden sm:inline text-sm font-medium ${colors.textPrimary}`}>
                    Volver a LoyalShift
                </span>
                 {/* Icon indicating action */}
                <FiLogOut className={`w-4 h-4 ${colors.textSecondary} ml-auto sm:ml-1`} />
            </motion.div>

            {/* Confirmation Dialog */}
            <AnimatePresence>
                {isDialogOpen && (
                    <motion.div
                        key="overlay"
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" // Changed overlay color
                        variants={modalOverlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={closeDialog} // Close on overlay click
                    >
                        <motion.div
                            key="dialog"
                            className={`relative w-full max-w-sm rounded-lg shadow-xl p-6 ${colors.background} border ${colors.border}`}
                            variants={modalContentVariants}
                            // Prevent closing when clicking inside the dialog
                            onClick={(e) => e.stopPropagation()}
                            role="alertdialog" // Use alertdialog for confirmations
                            aria-modal="true"
                            aria-labelledby="dialog-title"
                            aria-describedby="dialog-description"
                        >
                             {/* Icon and Title */}
                             <div className="flex items-center justify-center mb-4 text-center">
                                <FiAlertTriangle className={`w-10 h-10 text-yellow-500 mr-3`} />
                                <h2 id="dialog-title" className={`text-lg font-semibold ${colors.textPrimary}`}>Confirmar Navegación</h2>
                            </div>

                            {/* Description */}
                            <p id="dialog-description" className={`${colors.textSecondary} text-center text-sm mb-6`}>
                                ¿Seguro que desea salir de ANACO y volver a LoyalShift?
                            </p>

                            {/* Action Buttons */}
                            <div className="flex justify-center gap-3">
                                <button
                                    onClick={closeDialog}
                                    className={`px-4 py-2 rounded text-sm font-medium ${colors.cancelButtonBg} ${colors.textOnDarkButton} ${colors.cancelButtonHoverBg} transition-colors`}
                                    aria-label="Cancelar"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmNavigation}
                                    className={`px-4 py-2 rounded text-sm font-medium ${colors.confirmButtonBg} ${colors.textOnDarkButton} ${colors.confirmButtonHoverBg} transition-colors flex items-center gap-1.5`}
                                    aria-label="Confirmar navegación a LoyalShift"
                                >
                                     <FiCheck className="w-4 h-4"/> Confirmar
                                </button>
                            </div>
                            {/* Optional Close Button */}
                            <button
                                onClick={closeDialog}
                                className={`absolute top-2 right-2 p-1 rounded-full ${colors.textSecondary} hover:bg-slate-700 transition-colors`}
                                aria-label="Cerrar diálogo"
                            >
                               <FiX className="w-5 h-5"/>
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

BackToLoyalShiftToast.propTypes = {
    /** The URL to navigate back to (defaults to "/") */
    targetUrl: PropTypes.string,
};

export default BackToLoyalShiftToast;
